import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

import 'primeflex/primeflex.css';
import { ToastContext } from 'app/context/ToastContext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { createAppreciation, getAppreciation, updateAppreciation } from 'app/api/druzstvoAppreciationApi';
import { ErrorMessage, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';

const validationSchema = Yup.object({
  year: Yup.number().required('Rok je povinný'),
  appreciation: Yup.number().max(255, 'Max 255 znakov').required('Hodnota je povinná'),
});

const CreateDruzstvoAppreciation: React.FC = () => {
  const { id } = useParams<{ id?: string }>(); // Capture id from URL

  const [searchParamsInitial] = useSearchParams();
  const tempParameters = searchParamsInitial.get('p');

  const navigate = useNavigate();
  const { showSuccess, showError } = useContext(ToastContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (id) {
      getAppreciation(id)
        .then(response => {
          if (response.data) {
            setData(response.data);
          }
        })
        .catch(error => {
          console.error(error);
          showError('Chyba', error.response?.data?.detail);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const handleSubmit = (values: any) => {
    setIsSaving(true);

    let call;
    if (id) {
      call = updateAppreciation(id, values);
    } else {
      call = createAppreciation(values);
    }

    call
      .then(response => {
        showSuccess('Úspech', 'Zhodnotenie úspešne uložené.');
        setData(response.data);
      })
      .catch(error => {
        showError('Chyba', error.response?.data?.detail);
        console.error('Error creating agent:', error);
      })
      .finally(() => setIsSaving(false));
  };

  return (
    <div className="card">
      {loading && (
        <div className="flex justify-content-center">
          <ProgressSpinner />
        </div>
      )}

      {!loading && (
        <>
          <h2>{id ? 'Upraviť dáta zhodnotenia' : 'Vytvoriť nové zhodnotenie'}</h2>
          <Formik
            initialValues={
              data
                ? data
                : {
                    year: '',
                    appreciation: '',
                  }
            }
            validationSchema={validationSchema}
            onSubmit={values => {
              handleSubmit(values);
            }}
          >
            {({ isSubmitting, setFieldValue, values, errors }) => {
              return (
                <Form onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} className="p-fluid">
                  <div className="grid">
                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="year">Rok</label>
                        <InputText id={`year`} value={values?.year} onChange={e => void setFieldValue(`year`, e.target.value)} />
                        <ErrorMessage name="year" component="div" className="p-error" />
                      </div>
                    </div>
                    <div className="col-12 md:col-6">
                      <div className="p-field">
                        <label htmlFor="appreciation">Zhodnotenie za rok (%)</label>
                        <InputNumber
                          id={`appreciation`}
                          value={values?.appreciation}
                          locale="sk"
                          minFractionDigits={0}
                          maxFractionDigits={4}
                          onValueChange={e => void setFieldValue(`appreciation`, e.value)}
                        />
                        <ErrorMessage name="appreciation" component="div" className="p-error" />
                      </div>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px' }}>
                    <Button
                      type="button"
                      label="Späť"
                      icon="pi pi-arrow-left"
                      className="p-button-secondary"
                      onClick={() => navigate(`/druzstvo-appreciations?p=${tempParameters}`)}
                    />
                    <Button style={{ backgroundColor: '#e31818' }} type="submit" label="Uložiť" icon="pi pi-check" disabled={isSaving} />
                  </div>
                </Form>
              );
            }}
          </Formik>
        </>
      )}
    </div>
  );
};

export default CreateDruzstvoAppreciation;
